import type {
  Template,
  TemplateCreatePayload,
  TemplateIdResource,
  TemplateUpdateParams,
} from "@/types/entities/templates";
import type { SortDirectionType } from "@/types/common/filters";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import axios from "@/utils/axios";
import type { AxiosError } from "axios";

export interface GetDocumentTemplatesParams {
  page?: number;
  per_page?: number;
  order_by?: string;
  sort_direction?: SortDirectionType;
  search?: string;
  filters?: Record<string, string>;
}
interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
}

export const useGetTemplates = (params: GetDocumentTemplatesParams) => {
  return useQuery({
    queryKey: ["templates", params],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { page, per_page, order_by, sort_direction, search, filters } =
        params;

      const queryParams: Record<string, string | number> = {};

      if (page) queryParams.page = page;
      if (per_page) queryParams.per_page = per_page;

      if (order_by) {
        queryParams.order_by =
          sort_direction === "desc" ? `!${order_by}` : order_by;
      }

      if (search) {
        queryParams["filter[search]"] = search;
      }

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            queryParams[`filter[${key}]`] = value;
          }
        });
      }

      const response = await axios
        .get("/admin/v1/generator-template", {
          params: queryParams,
        })
        .catch((error) => {
          console.error("Error 422 detalles:", error.response?.data);
          throw error;
        });
      return response.data;
    },
  });
};

export const useGetTemplate = (id?: string) => {
  return useQuery({
    queryKey: ["template", id],
    queryFn: async (): Promise<TemplateIdResource> => {
      const response = await axios.get(`/admin/v1/generator-template/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useGetTemplateVersions = (parentTemplateId?: string) => {
  return useQuery<{
    data: Array<{
      id: string;
      version: number;
      display_name: string;
      created_at: string;
      is_latest: boolean;
      latest_version: number;
    }>;
  }>({
    queryKey: ["template-versions", parentTemplateId],
    queryFn: async () => {
      const response = await axios.get(
        `/admin/v1/generator-template?filter[parent_template_id]=${parentTemplateId}`,
      );
      return response.data;
    },
    enabled: !!parentTemplateId,
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Template,
    AxiosError<ApiErrorResponse>,
    TemplateCreatePayload
  >({
    mutationFn: async (data: TemplateCreatePayload) => {
      const response = await axios.post("/admin/v1/generator-template", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["templates"],
      });
    },
  });
};

export const useActivateTemplate = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.patch(
        `/admin/v1/generator-template/${id}/activate`,
      );
      return response.data;
    },
  });
};

export const useDeactivateTemplate = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.patch(
        `/admin/v1/generator-template/${id}/deactivate`,
      );
      return response.data;
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: TemplateUpdateParams) => {
      const updateData = {
        display_name: data.display_name,
        description: data.description,
        mapping_name: data.mapping_name,
        rich_text: data.rich_text,
        variables: data.variables,
      };
      const response = await axios.put(
        `/admin/v1/generator-template/${id}`,
        updateData,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["templates"],
      });
    },
  });
};
